<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Date Range Selector -->
      <div
        class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8"
      >
        <div
          class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
        >
          <div class="flex items-center space-x-4">
            <div class="relative">
              <select
                v-model="dateRange"
                class="pl-4 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last90">Last 90 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="custom">Custom Range</option>
              </select>
              <svg
                class="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            <div
              v-if="dateRange === 'custom'"
              class="flex items-center space-x-2"
            >
              <input
                type="date"
                v-model="customStartDate"
                class="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span class="text-gray-500">to</span>
              <input
                type="date"
                v-model="customEndDate"
                class="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div class="flex items-center space-x-3">
            <button
              @click="exportReport"
              class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              EXPORT REPORT
            </button>
            <button
              @click="refreshData"
              class="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Refresh data"
            >
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Key Metrics -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500">Total Cases</span>
            <span
              class="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"
              >+12.5%</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900">{{
              formatNumber(metrics.totalCases)
            }}</span>
            <span class="text-sm text-gray-500"
              >vs {{ formatNumber(metrics.previousTotal) }}</span
            >
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center text-sm">
              <span class="text-gray-600">Avg. per day:</span>
              <span class="ml-2 font-semibold text-gray-900">{{
                formatNumber(metrics.avgDailyCases)
              }}</span>
            </div>
          </div>
        </div>

        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500"
              >Verification Rate</span
            >
            <span
              class="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"
              >+5.2%</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900"
              >{{ metrics.verificationRate }}%</span
            >
            <span class="text-sm text-gray-500">target 85%</span>
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-green-500 h-2 rounded-full"
                :style="{ width: metrics.verificationRate + '%' }"
              ></div>
            </div>
          </div>
        </div>

        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500"
              >Avg. Response Time</span
            >
            <span
              class="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full"
              >+2.3%</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900">{{
              metrics.avgResponseTime
            }}</span>
            <span class="text-sm text-gray-500"
              >vs {{ metrics.previousResponseTime }}</span
            >
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center text-sm">
              <span class="text-gray-600">Fastest:</span>
              <span class="ml-2 font-semibold text-green-600">{{
                metrics.fastestTime
              }}</span>
              <span class="ml-auto text-gray-600">Slowest:</span>
              <span class="ml-2 font-semibold text-red-600">{{
                metrics.slowestTime
              }}</span>
            </div>
          </div>
        </div>

        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500"
              >Customer Satisfaction</span
            >
            <span
              class="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"
              >+1.8%</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900"
              >{{ metrics.csat }}%</span
            >
            <span class="text-sm text-gray-500">based on 234 reviews</span>
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-600">5-star ratings:</span>
              <span class="font-semibold text-gray-900">{{
                metrics.fiveStarCount
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <!-- Cases Over Time Chart -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Cases Over Time</h3>
            <div class="flex items-center space-x-3">
              <div class="flex items-center">
                <span class="w-3 h-3 bg-blue-500 rounded-full mr-1"></span>
                <span class="text-xs text-gray-500">New</span>
              </div>
              <div class="flex items-center">
                <span class="w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                <span class="text-xs text-gray-500">Verified</span>
              </div>
              <div class="flex items-center">
                <span class="w-3 h-3 bg-red-500 rounded-full mr-1"></span>
                <span class="text-xs text-gray-500">Rejected</span>
              </div>
            </div>
          </div>

          <div class="h-80 relative">
            <!-- Chart Container -->
            <div class="absolute inset-0 flex items-end justify-around px-4">
              <div
                v-for="(day, index) in casesOverTime"
                :key="index"
                class="flex-1 flex flex-col items-center group"
              >
                <!-- Tooltip -->
                <div
                  class="absolute bottom-full mb-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none"
                >
                  {{ day.date }}<br />
                  New: {{ day.new }} | Verified: {{ day.verified }} | Rejected:
                  {{ day.rejected }}
                </div>

                <!-- Stacked Bars -->
                <div class="w-8 flex flex-col items-center space-y-1">
                  <!-- Rejected bar (top) -->
                  <div
                    class="w-full bg-gradient-to-t from-red-500 to-red-400 rounded-t-lg transition-all duration-300"
                    :style="{ height: day.rejected * 3 + 'px' }"
                  ></div>
                  <!-- Verified bar (middle) -->
                  <div
                    class="w-full bg-gradient-to-t from-green-500 to-green-400 transition-all duration-300"
                    :style="{ height: day.verified * 3 + 'px' }"
                  ></div>
                  <!-- New bar (bottom) -->
                  <div
                    class="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-b-lg transition-all duration-300"
                    :style="{ height: day.new * 3 + 'px' }"
                  ></div>
                </div>

                <!-- Date label -->
                <span
                  class="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left"
                  >{{ formatShortDate(day.date) }}</span
                >
              </div>
            </div>

            <!-- Grid lines -->
            <div
              class="absolute inset-0 flex flex-col justify-between pointer-events-none"
            >
              <div
                v-for="i in 5"
                :key="i"
                class="border-b border-gray-100 w-full h-16"
              ></div>
            </div>
          </div>
        </div>

        <!-- Verification Outcomes Pie Chart -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-6">
            Verification Outcomes
          </h3>

          <div class="flex items-center justify-center">
            <!-- Donut Chart -->
            <div class="relative w-64 h-64">
              <svg
                viewBox="0 0 100 100"
                class="transform -rotate-90 w-full h-full"
              >
                <!-- Background circle -->
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#f3f4f6"
                  stroke-width="12"
                />

                <!-- Verified segment -->
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#10b981"
                  stroke-width="12"
                  stroke-dasharray="251.2"
                  :stroke-dashoffset="
                    251.2 - (251.2 * metrics.outcomePercentages.verified) / 100
                  "
                  stroke-linecap="round"
                  class="transition-all duration-1000"
                />

                <!-- Rejected segment -->
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#ef4444"
                  stroke-width="12"
                  stroke-dasharray="251.2"
                  :stroke-dashoffset="
                    251.2 - (251.2 * metrics.outcomePercentages.rejected) / 100
                  "
                  stroke-linecap="round"
                  class="transition-all duration-1000"
                  :style="{
                    transform: `rotate(${
                      metrics.outcomePercentages.verified * 3.6
                    }deg)`,
                    transformOrigin: 'center',
                  }"
                />

                <!-- Pending segment -->
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#f59e0b"
                  stroke-width="12"
                  stroke-dasharray="251.2"
                  :stroke-dashoffset="
                    251.2 - (251.2 * metrics.outcomePercentages.pending) / 100
                  "
                  stroke-linecap="round"
                  class="transition-all duration-1000"
                  :style="{
                    transform: `rotate(${
                      (metrics.outcomePercentages.verified +
                        metrics.outcomePercentages.rejected) *
                      3.6
                    }deg)`,
                    transformOrigin: 'center',
                  }"
                />
              </svg>

              <!-- Center text -->
              <div
                class="absolute inset-0 flex flex-col items-center justify-center"
              >
                <span class="text-3xl font-bold text-gray-900">{{
                  metrics.totalCases
                }}</span>
                <span class="text-xs text-gray-500">Total Cases</span>
              </div>
            </div>
          </div>

          <!-- Legend -->
          <div class="grid grid-cols-3 gap-4 mt-6">
            <div class="text-center">
              <div class="flex items-center justify-center mb-1">
                <span class="w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                <span class="text-sm font-medium text-gray-700">Verified</span>
              </div>
              <span class="text-lg font-bold text-gray-900">{{
                metrics.outcomeCounts.verified
              }}</span>
              <span class="text-xs text-gray-500 block"
                >{{ metrics.outcomePercentages.verified }}%</span
              >
            </div>
            <div class="text-center">
              <div class="flex items-center justify-center mb-1">
                <span class="w-3 h-3 bg-red-500 rounded-full mr-1"></span>
                <span class="text-sm font-medium text-gray-700">Rejected</span>
              </div>
              <span class="text-lg font-bold text-gray-900">{{
                metrics.outcomeCounts.rejected
              }}</span>
              <span class="text-xs text-gray-500 block"
                >{{ metrics.outcomePercentages.rejected }}%</span
              >
            </div>
            <div class="text-center">
              <div class="flex items-center justify-center mb-1">
                <span class="w-3 h-3 bg-yellow-500 rounded-full mr-1"></span>
                <span class="text-sm font-medium text-gray-700">Pending</span>
              </div>
              <span class="text-lg font-bold text-gray-900">{{
                metrics.outcomeCounts.pending
              }}</span>
              <span class="text-xs text-gray-500 block"
                >{{ metrics.outcomePercentages.pending }}%</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Second Row -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <!-- Officer Performance -->
        <div
          class="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">
              Officer Performance
            </h3>
            <select
              v-model="performanceMetric"
              class="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="cases">By Cases Handled</option>
              <option value="time">By Avg. Time</option>
              <option value="success">By Success Rate</option>
            </select>
          </div>

          <div class="space-y-4">
            <div
              v-for="officer in officerPerformance"
              :key="officer.id"
              class="flex items-center"
            >
              <div class="w-40 flex items-center">
                <img
                  :src="officer.avatar"
                  :alt="officer.name"
                  class="w-8 h-8 rounded-lg object-cover mr-2"
                />
                <span class="text-sm font-medium text-gray-700 truncate">{{
                  officer.name
                }}</span>
              </div>

              <div class="flex-1 mx-4">
                <div class="relative">
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div
                      class="h-2 rounded-full transition-all duration-500"
                      :class="{
                        'bg-blue-500': performanceMetric === 'cases',
                        'bg-green-500': performanceMetric === 'success',
                        'bg-purple-500': performanceMetric === 'time',
                      }"
                      :style="{
                        width: getOfficerPerformanceValue(officer) + '%',
                      }"
                    ></div>
                  </div>
                </div>
              </div>

              <div class="w-20 text-right">
                <span class="text-sm font-semibold text-gray-900">{{
                  getOfficerPerformanceDisplay(officer)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Call Outcome Distribution -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-6">
            Call Outcomes
          </h3>

          <div class="space-y-4">
            <div
              v-for="outcome in callOutcomes"
              :key="outcome.label"
              class="flex items-center justify-between"
            >
              <div class="flex items-center">
                <span
                  :class="['w-3 h-3 rounded-full mr-2', outcome.colorClass]"
                ></span>
                <span class="text-sm text-gray-600">{{ outcome.label }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-sm font-semibold text-gray-900">{{
                  outcome.count
                }}</span>
                <span class="text-xs text-gray-500 w-12 text-right"
                  >{{ outcome.percentage }}%</span
                >
              </div>
            </div>
          </div>

          <div class="mt-6 pt-6 border-t border-gray-100">
            <div class="flex justify-between text-sm">
              <span class="text-gray-500">Total Call Attempts</span>
              <span class="font-bold text-gray-900">{{
                metrics.totalCallAttempts
              }}</span>
            </div>
            <div class="flex justify-between text-sm mt-2">
              <span class="text-gray-500">Avg. Attempts per Case</span>
              <span class="font-bold text-gray-900">{{
                metrics.avgAttemptsPerCase
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Third Row - Detailed Tables -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Top Performing Officers -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            Top Performing Officers
          </h3>
          <table class="w-full">
            <thead class="text-xs text-gray-500 uppercase">
              <tr>
                <th class="text-left pb-3">Officer</th>
                <th class="text-right pb-3">Cases</th>
                <th class="text-right pb-3">Success Rate</th>
                <th class="text-right pb-3">Avg. Time</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="officer in topOfficers"
                :key="officer.id"
                class="text-sm"
              >
                <td class="py-2">
                  <div class="flex items-center">
                    <img
                      :src="officer.avatar"
                      :alt="officer.name"
                      class="w-6 h-6 rounded mr-2"
                    />
                    {{ officer.name }}
                  </div>
                </td>
                <td class="text-right font-medium">{{ officer.cases }}</td>
                <td class="text-right">
                  <span class="text-green-600">{{ officer.successRate }}%</span>
                </td>
                <td class="text-right text-gray-600">{{ officer.avgTime }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Recent Activity -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div class="space-y-4">
            <div
              v-for="activity in recentActivity"
              :key="activity.id"
              class="flex items-start space-x-3"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  activity.iconBg,
                ]"
              >
                <svg
                  class="w-4 h-4"
                  :class="activity.iconColor"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    :d="activity.icon"
                  />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm text-gray-900">{{ activity.message }}</p>
                <div class="flex items-center mt-1">
                  <span class="text-xs text-gray-500">{{ activity.time }}</span>
                  <span class="mx-2 text-gray-300">•</span>
                  <span class="text-xs text-gray-500">{{ activity.user }}</span>
                </div>
              </div>
              <StatusBadge v-if="activity.status" :status="activity.status" />
            </div>
          </div>
        </div>
      </div>

      <!-- Export Modal -->
      <div
        v-if="showExportModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            @click="showExportModal = false"
          ></div>

          <span class="hidden sm:inline-block sm:align-middle sm:h-screen"
            >&#8203;</span
          >

          <div
            class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3
                class="text-lg font-semibold text-gray-900 mb-4"
                id="modal-title"
              >
                Export Report
              </h3>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Export Format</label
                  >
                  <div class="flex space-x-4">
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="exportFormat"
                        value="pdf"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700">PDF</span>
                    </label>
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="exportFormat"
                        value="excel"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700">Excel</span>
                    </label>
                    <label class="flex items-center">
                      <input
                        type="radio"
                        v-model="exportFormat"
                        value="csv"
                        class="mr-2"
                      />
                      <span class="text-sm text-gray-700">CSV</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Include Sections</label
                  >
                  <div class="space-y-2">
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        v-model="exportSections.summary"
                        class="mr-2 rounded border-gray-300 text-blue-600"
                      />
                      <span class="text-sm text-gray-700"
                        >Executive Summary</span
                      >
                    </label>
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        v-model="exportSections.metrics"
                        class="mr-2 rounded border-gray-300 text-blue-600"
                      />
                      <span class="text-sm text-gray-700">Key Metrics</span>
                    </label>
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        v-model="exportSections.charts"
                        class="mr-2 rounded border-gray-300 text-blue-600"
                      />
                      <span class="text-sm text-gray-700"
                        >Charts & Visualizations</span
                      >
                    </label>
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        v-model="exportSections.detailed"
                        class="mr-2 rounded border-gray-300 text-blue-600"
                      />
                      <span class="text-sm text-gray-700">Detailed Data</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >Date Range</label
                  >
                  <div class="flex items-center space-x-2">
                    <input
                      type="date"
                      v-model="exportStartDate"
                      class="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    />
                    <span class="text-gray-500">to</span>
                    <input
                      type="date"
                      v-model="exportEndDate"
                      class="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-6 flex justify-end space-x-3">
                <button
                  @click="showExportModal = false"
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  @click="generateExport"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Generate Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import StatusBadge from "@/components/ui/StatusBadge.vue";

// Date range state
const dateRange = ref("last30");
const customStartDate = ref("");
const customEndDate = ref("");
const showExportModal = ref(false);
const exportFormat = ref("pdf");
const exportSections = ref({
  summary: true,
  metrics: true,
  charts: true,
  detailed: false,
});
const exportStartDate = ref("");
const exportEndDate = ref("");

// Performance metric selector
const performanceMetric = ref("cases");

// Mock metrics data
const metrics = ref({
  totalCases: 1254,
  previousTotal: 1115,
  avgDailyCases: 42,
  verificationRate: 87.5,
  csat: 94.2,
  fiveStarCount: 198,
  avgResponseTime: "2.4h",
  previousResponseTime: "2.1h",
  fastestTime: "45m",
  slowestTime: "5.2h",
  totalCallAttempts: 3421,
  avgAttemptsPerCase: 2.7,
  outcomeCounts: {
    verified: 876,
    rejected: 245,
    pending: 133,
  },
  outcomePercentages: {
    verified: 70,
    rejected: 19.5,
    pending: 10.5,
  },
});

// Cases over time data
const casesOverTime = ref([
  { date: "2026-02-19", new: 38, verified: 32, rejected: 6 },
  { date: "2026-02-20", new: 42, verified: 35, rejected: 7 },
  { date: "2026-02-21", new: 35, verified: 30, rejected: 5 },
  { date: "2026-02-22", new: 44, verified: 38, rejected: 6 },
  { date: "2026-02-23", new: 48, verified: 41, rejected: 7 },
  { date: "2026-02-24", new: 52, verified: 45, rejected: 7 },
  { date: "2026-02-25", new: 41, verified: 36, rejected: 5 },
  { date: "2026-02-26", new: 39, verified: 33, rejected: 6 },
  { date: "2026-02-27", new: 45, verified: 38, rejected: 7 },
  { date: "2026-02-28", new: 47, verified: 40, rejected: 7 },
  { date: "2026-03-01", new: 43, verified: 37, rejected: 6 },
  { date: "2026-03-02", new: 46, verified: 39, rejected: 7 },
]);

// Call outcomes distribution
const callOutcomes = ref([
  {
    label: "Reached",
    count: 1245,
    percentage: 36.4,
    colorClass: "bg-green-500",
  },
  {
    label: "No Answer",
    count: 856,
    percentage: 25.0,
    colorClass: "bg-yellow-500",
  },
  {
    label: "Call Back Later",
    count: 543,
    percentage: 15.9,
    colorClass: "bg-blue-500",
  },
  {
    label: "Wrong Number",
    count: 321,
    percentage: 9.4,
    colorClass: "bg-red-500",
  },
  {
    label: "Denied Relationship",
    count: 234,
    percentage: 6.8,
    colorClass: "bg-orange-500",
  },
  {
    label: "Language Barrier",
    count: 122,
    percentage: 3.6,
    colorClass: "bg-purple-500",
  },
  { label: "Other", count: 100, percentage: 2.9, colorClass: "bg-gray-500" },
]);

// Officer performance data
const officerPerformance = ref([
  {
    id: 1,
    name: "John Doe",
    avatar:
      "https://ui-avatars.com/api/?name=John+Doe&background=3b82f6&color=fff",
    cases: 142,
    successRate: 94,
    avgTime: "1.8h",
    casesPercent: 100,
    timePercent: 85,
    successPercent: 98,
  },
  {
    id: 2,
    name: "Jane Smith",
    avatar:
      "https://ui-avatars.com/api/?name=Jane+Smith&background=10b981&color=fff",
    cases: 128,
    successRate: 91,
    avgTime: "2.1h",
    casesPercent: 90,
    timePercent: 72,
    successPercent: 95,
  },
  {
    id: 3,
    name: "Mike Johnson",
    avatar:
      "https://ui-avatars.com/api/?name=Mike+Johnson&background=8b5cf6&color=fff",
    cases: 115,
    successRate: 88,
    avgTime: "2.3h",
    casesPercent: 81,
    timePercent: 66,
    successPercent: 92,
  },
  {
    id: 4,
    name: "Sarah Williams",
    avatar:
      "https://ui-avatars.com/api/?name=Sarah+Williams&background=ec4899&color=fff",
    cases: 98,
    successRate: 92,
    avgTime: "1.9h",
    casesPercent: 69,
    timePercent: 80,
    successPercent: 96,
  },
  {
    id: 5,
    name: "David Brown",
    avatar:
      "https://ui-avatars.com/api/?name=David+Brown&background=f59e0b&color=fff",
    cases: 87,
    successRate: 85,
    avgTime: "2.5h",
    casesPercent: 61,
    timePercent: 60,
    successPercent: 89,
  },
]);

// Top performing officers
const topOfficers = computed(() => {
  return [...officerPerformance.value]
    .sort((a, b) => b.cases - a.cases)
    .slice(0, 5);
});

// Recent activity
const recentActivity = ref([
  {
    id: 1,
    message: "Case #827373 was verified successfully",
    time: "5 minutes ago",
    user: "John Doe",
    status: "verified",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
  },
  {
    id: 2,
    message: "New case #827542 assigned to verification",
    time: "15 minutes ago",
    user: "System",
    status: "pending",
    icon: "M12 6v6m0 0v6m0-6h6m-6 0H6",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
  },
  {
    id: 3,
    message: "Case #827229 was rejected due to invalid documents",
    time: "32 minutes ago",
    user: "Jane Smith",
    status: "rejected",
    icon: "M6 18L18 6M6 6l12 12",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
  },
  {
    id: 4,
    message: "Bulk verification completed for 15 cases",
    time: "1 hour ago",
    user: "Mike Johnson",
    status: "completed",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  {
    id: 5,
    message: "New verification officer Sarah Williams joined",
    time: "2 hours ago",
    user: "Admin",
    status: null,
    icon: "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z",
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-600",
  },
]);

// Helper functions
const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

const formatShortDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getOfficerPerformanceValue = (officer) => {
  switch (performanceMetric.value) {
    case "cases":
      return officer.casesPercent;
    case "time":
      return officer.timePercent;
    case "success":
      return officer.successPercent;
    default:
      return officer.casesPercent;
  }
};

const getOfficerPerformanceDisplay = (officer) => {
  switch (performanceMetric.value) {
    case "cases":
      return officer.cases + " cases";
    case "time":
      return officer.avgTime;
    case "success":
      return officer.successRate + "%";
    default:
      return officer.cases + " cases";
  }
};

// Actions
const refreshData = () => {
  // Simulate data refresh
  console.log("Refreshing data...");
};

const exportReport = () => {
  showExportModal.value = true;
  // Set default export dates
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  exportStartDate.value = thirtyDaysAgo.toISOString().split("T")[0];
  exportEndDate.value = today.toISOString().split("T")[0];
};

const generateExport = () => {
  console.log("Generating export:", {
    format: exportFormat.value,
    sections: exportSections.value,
    dateRange: {
      start: exportStartDate.value,
      end: exportEndDate.value,
    },
  });
  showExportModal.value = false;

  // Show success message (you could add a toast notification here)
  alert(`Report exported successfully as ${exportFormat.value.toUpperCase()}`);
};

// Watch for date range changes
watch(dateRange, (newVal) => {
  if (newVal !== "custom") {
    // Update metrics based on selected range
    console.log("Date range changed:", newVal);
  }
});

// Initialize
onMounted(() => {
  // Set default custom dates
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  customStartDate.value = thirtyDaysAgo.toISOString().split("T")[0];
  customEndDate.value = today.toISOString().split("T")[0];
});
</script>
